import React, { useEffect, useMemo, useState } from "react";
import { Property } from "../types";
import { isSyntheticParcelId } from "../lib/dataQuality";

// Referrer-restricted browser key (Vercel env). Used ONLY to render Street View
// images client-side; the server never emits a key-bearing URL. Safe to ship in
// the bundle because the key is restricted to the parcelyapp.com referrer.
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

function buildIlookaboutUrl(parcelId: string): string {
  return `https://ssproxy.ilookabout.com/?Partner=MecklenburgNC&PIN=${parcelId}&w=1200&h=900&1cf=1&bg=white`;
}

function buildSatelliteUrl(lat: number, lon: number): string {
  const pad = 0.002; // ~200m buffer
  const bbox = `${lon - pad},${lat - pad},${lon + pad},${lat + pad}`;
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export?bbox=${bbox}&bboxSR=4326&size=1200,400&format=jpg&f=image`;
}

// buildStreetViewUrl renders a Street View Static image from a panorama id +
// heading (computed server-side toward the house). The browser key is required.
export function buildStreetViewUrl(pano: string, heading: number | null | undefined): string {
  const h = heading != null ? Math.round(heading) : 0;
  return (
    `https://maps.googleapis.com/maps/api/streetview?size=1200x900` +
    `&pano=${encodeURIComponent(pano)}&heading=${h}&pitch=0&fov=80&key=${GOOGLE_KEY}`
  );
}

export interface PhotoCandidate {
  src: string;
  kind: "bpo" | "ilookabout" | "streetview" | "mapillary" | "aerial";
  attribution?: string;
}

// buildCandidates is the photo fallback chain, best → worst:
// county BPO photo → Mecklenburg iLookAbout → resolved street-level (Street View
// or Mapillary) → ArcGIS aerial. The first that loads wins; onError advances.
export function buildCandidates(property: Property): PhotoCandidate[] {
  const out: PhotoCandidate[] = [];
  if (property.bpoUrl) {
    out.push({ src: property.bpoUrl, kind: "bpo" });
  }
  // A synthetic CASE:{court_file_number} parcelId (#687 / ADR 0023) isn't a
  // real PIN — building an iLookAbout URL from it would request a photo for
  // a parcel that doesn't exist, always erroring and burning a fallback
  // slot for nothing.
  if (property.countyId === "mecklenburg-nc" && !isSyntheticParcelId(property.parcelId)) {
    out.push({ src: buildIlookaboutUrl(property.parcelId), kind: "ilookabout" });
  }
  const pi = property.primaryImage;
  if (pi) {
    if (pi.provider === "google_streetview" && pi.ref && GOOGLE_KEY) {
      out.push({ src: buildStreetViewUrl(pi.ref, pi.heading), kind: "streetview" });
    } else if (pi.provider === "mapillary" && pi.url) {
      out.push({ src: pi.url, kind: "mapillary", attribution: pi.attribution });
    }
  }
  if (property.latitude && property.longitude) {
    out.push({ src: buildSatelliteUrl(property.latitude, property.longitude), kind: "aerial" });
  }
  return out;
}

interface PhotoGalleryProps {
  property: Property;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ property }) => {
  const candidates = useMemo(() => buildCandidates(property), [property]);
  // Index into the fallback chain; advances on image load error. Reset whenever
  // the property changes (the modal may reuse this instance).
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [property.id]);

  const current = idx < candidates.length ? candidates[idx] : undefined;

  if (!current) {
    return (
      <div className="photo-gallery">
        <div className="photo-gallery-placeholder">
          <span className="photo-gallery-placeholder-icon">🏠</span>
          <span>No photo available</span>
        </div>
        <div className="photo-gallery-gradient" />
      </div>
    );
  }

  const isAerial = current.kind === "aerial";
  return (
    <div className="photo-gallery">
      <img
        src={current.src}
        alt={`${isAerial ? "Satellite view of" : "Property at"} ${property.address}`}
        className={`photo-gallery-img${isAerial ? " photo-gallery-img--satellite" : ""}`}
        onError={() => setIdx((i) => i + 1)}
      />
      {current.attribution ? (
        <div className="photo-gallery-attribution">{current.attribution}</div>
      ) : null}
      <div className="photo-gallery-gradient" />
    </div>
  );
};

export default PhotoGallery;
